import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { companyInfo } from '../../utils/dataInfo';

// Helper functions
const formatCurrency = (amount) => {
return new Intl.NumberFormat('en-US', {
    // style: 'currency',
    // currency: 'USD'
}).format(amount || 0) + " "+"sh";
};

const formatNumber = (num) => {
return new Intl.NumberFormat('en-US').format(num || 0);
};

const formatDate = (date) => {
return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
});
};

const formatDateTime = (date) => {
return new Date(date).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
});
};

const getStatusColor = (status) => {
switch (status?.toLowerCase()) {
    case 'completed': return '#4caf50';
    case 'pending': return '#ff9800';
    case 'cancelled': return '#f44336';
    case 'quotation': return '#2196f3';
    default: return '#757575';
}
};

const getPaymentMethodColor = (method) => {
switch (method?.toLowerCase()) {
    case 'cash': return '#4caf50';
    case 'mobile_money': return '#2196f3';
    case 'bank': return '#3f51b5';
    case 'pending': return '#ff9800';
    default: return '#757575';
}
};

const formatOrderNumber = (orderNumber) => {
if (!orderNumber) return 'N/A';
// Remove any existing dashes and format consistently
const clean = orderNumber.replace(/-/g, '');
return clean.replace(/(.{4})/g, '$1-').slice(0, -1);
};

// Generate Excel Report
export const exportBarOrdersToExcel = async (reportData, filters = {}) => {
try {
    const workbook = XLSX.utils.book_new();
    
    // Main Sheet - Updated to remove branch and servant
    const mainHeaders = [
    'Order No.',
    'Date/Time',
    'Customer Type',
    'Total Amount',
    'Payment',
    'Status',
    'Order Notes'
    ];

    const mainData = reportData.orders?.map(order => [
    formatOrderNumber(order.order_number),
    formatDateTime(order.created_at),
    order.customer_type?.name || 'N/A',
    order.total_amount,
    order.payment_method ? order.payment_method.toUpperCase().replace('_', ' ') : 'PENDING',
    order.order_status ? order.order_status.toUpperCase() : 'PENDING',
    order.order_notes || ''
    ]) || [];

    const mainSheetData = [mainHeaders, ...mainData];
    const mainSheet = XLSX.utils.aoa_to_sheet(mainSheetData);
    XLSX.utils.book_append_sheet(workbook, mainSheet, 'Bar Orders');

    // Summary Sheet - Updated
    const summarySheetData = [
    ['BAR ORDERS REPORT SUMMARY'],
    ['Generated on', new Date().toLocaleString()],
    [''],
    ['Total Orders', formatNumber(reportData.summary.total_orders)],
    ['Total Sales', formatCurrency(reportData.summary.total_sales)],
    ['Total Items Sold', formatNumber(reportData.summary.total_items)],
    ['Average Order Value', formatCurrency(reportData.summary.avg_order_value)],
    [''],
    ['FILTERS APPLIED'],
    ['Customer Type', filters.customer_type_name || 'All'],
    ['Payment Method', filters.payment_method || 'All'],
    ['Order Status', filters.order_status || 'All'],
    ['Date From', filters.date_from || 'All'],
    ['Date To', filters.date_to || 'All']
    ];

    const summarySheet = XLSX.utils.aoa_to_sheet(summarySheetData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // Items Sheet
    const allItems = [];
    reportData.orders?.forEach(order => {
    order.items?.forEach(item => {
        allItems.push({
        'Order No.': formatOrderNumber(order.order_number),
        'Item Name': item.stock_name || item.name || 'N/A',
        'Quantity': item.quantity,
        'Unit Price': item.price,
        'Total Amount': item.total_amount,
        'Order Date': formatDate(order.created_at),
        'Customer Type': order.customer_type?.name || 'N/A'
        });
    });
    });

    if (allItems.length > 0) {
    const itemsSheet = XLSX.utils.json_to_sheet(allItems);
    XLSX.utils.book_append_sheet(workbook, itemsSheet, 'Items');
    }

    // Top Customers Sheet
    if (reportData.top_customers && reportData.top_customers.length > 0) {
    const customersData = reportData.top_customers.map(customer => ({
        'Customer Name': customer.customer_name,
        'Customer Type': customer.customer_type,
        'Total Orders': customer.order_count,
        'Total Spent': formatCurrency(customer.total_spent),
        'Average Order Value': formatCurrency(customer.avg_order_value),
        'Last Order Date': formatDate(customer.last_order_date)
    }));

    const customersSheet = XLSX.utils.json_to_sheet(customersData);
    XLSX.utils.book_append_sheet(workbook, customersSheet, 'Top Customers');
    }

    // Top Items Sheet
    if (reportData.summary.top_items && reportData.summary.top_items.length > 0) {
    const topItemsData = reportData.summary.top_items.map(item => ({
        'Item Name': item.stock_name,
        'Quantity Sold': item.total_quantity,
        'Total Sales': item.total_sales,
        'Average Price': item.avg_price,
        '% of Sales': ((item.total_sales / reportData.summary.total_sales) * 100).toFixed(1) + '%'
    }));

    const topItemsSheet = XLSX.utils.json_to_sheet(topItemsData);
    XLSX.utils.book_append_sheet(workbook, topItemsSheet, 'Top Items');
    }

    // Set column widths
    const sheets = workbook.SheetNames;
    sheets.forEach(sheetName => {
    const worksheet = workbook.Sheets[sheetName];
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    const colWidths = [];
    
    for (let col = range.s.c; col <= range.e.c; col++) {
        let maxWidth = 0;
        for (let row = range.s.r; row <= range.e.r; row++) {
        const cell = worksheet[XLSX.utils.encode_cell({ r: row, c: col })];
        if (cell && cell.v) {
            const cellWidth = String(cell.v).length;
            maxWidth = Math.max(maxWidth, cellWidth);
        }
        }
        colWidths.push({ wch: Math.min(maxWidth + 2, 50) });
    }
    
    worksheet['!cols'] = colWidths;
    });

    // Generate file name
    const fileName = `bar_orders_report_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    return { success: true, fileName };
} catch (error) {
    console.error('Excel export failed:', error);
    throw new Error('Failed to export to Excel: ' + error.message);
}
};

// Generate PDF Report - Updated with requested columns and without Executive Summary
export const exportBarOrdersToPDF = async (reportData, filters = {}) => {
try {
    const doc = new jsPDF('landscape');
    
    // Set document properties
    doc.setProperties({
    title: 'Bar Orders Report',
    subject: 'Bar Orders and Sales Management Report',
    author: companyInfo.name,
    keywords: 'bar orders, sales, report, customers',
    creator: companyInfo.name
    });

    // Title Page
    doc.setFontSize(24);
    doc.setTextColor(33, 150, 243);
    doc.text('BAR ORDERS REPORT', 105, 50, { align: 'center' });
    
    doc.setFontSize(16);
    doc.setTextColor(66, 66, 66);
    doc.text(companyInfo.name, 105, 65, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setTextColor(97, 97, 97);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 105, 75, { align: 'center' });
    
    if (filters.date_from && filters.date_to) {
    doc.text(`Period: ${formatDate(filters.date_from)} to ${formatDate(filters.date_to)}`, 105, 82, { align: 'center' });
    }

    // Main Table - Updated with requested columns
    doc.addPage();
    doc.setFontSize(18);
    doc.setTextColor(33, 150, 243);
    doc.text('BAR ORDER DETAILS', 20, 20);

    const tableData = reportData.orders?.map(order => [
    formatOrderNumber(order.order_number),
    formatDateTime(order.created_at),
    order.customer_type?.name || 'N/A',
    formatCurrency(order.total_amount),
    order.payment_method ? order.payment_method.toUpperCase().replace('_', ' ') : 'PENDING',
    order.order_status ? order.order_status.toUpperCase() : 'PENDING'
    ]) || [];

    autoTable(doc, {
    startY: 30,
    head: [['Order No.', 'Date/Time', 'Customer Type', 'Total Amount', 'Payment', 'Status']],
    body: tableData,
    theme: 'striped',
    headStyles: { fillColor: [33, 150, 243], textColor: 255, fontSize: 11 },
    columnStyles: {
        0: { cellWidth: 45 },
        1: { cellWidth: 45 },
        2: { cellWidth: 35 },
        3: { cellWidth: 30, halign: 'right' },
        4: { cellWidth: 30 },
        5: { cellWidth: 30 }
    },
    didParseCell: (data) => {
        // Style the amount column
        if (data.column.index === 3 && data.cell.raw) {
        data.cell.styles.fontStyle = 'bold';
        }
        
        // Color code payment method
        if (data.column.index === 4 && data.cell.raw) {
        const color = getPaymentMethodColor(data.cell.raw.toLowerCase());
        data.cell.styles.textColor = color;
        data.cell.styles.fontStyle = 'bold';
        }
        
        // Color code status
        if (data.column.index === 5 && data.cell.raw) {
        const color = getStatusColor(data.cell.raw.toLowerCase());
        data.cell.styles.textColor = color;
        data.cell.styles.fontStyle = 'bold';
        }
    }
    });

    // Payment Method Distribution
    if (reportData.summary.by_payment_method && Object.keys(reportData.summary.by_payment_method).length > 0) {
    doc.addPage();
    doc.setFontSize(18);
    doc.setTextColor(33, 150, 243);
    doc.text('PAYMENT METHOD DISTRIBUTION', 20, 20);

    const paymentData = Object.entries(reportData.summary.by_payment_method).map(([method, data]) => [
        method.toUpperCase().replace('_', ' '),
        data.count,
        formatCurrency(data.total_sales),
        formatCurrency(data.avg_order_value)
    ]);

    autoTable(doc, {
        startY: 30,
        head: [['Payment Method', 'Order Count', 'Total Sales', 'Avg Order Value']],
        body: paymentData,
        theme: 'grid',
        headStyles: { fillColor: [76, 175, 80], textColor: 255 },
        columnStyles: {
        2: { halign: 'right' },
        3: { halign: 'right' }
        },
        didParseCell: (data) => {
        if (data.column.index === 0 && data.cell.raw) {
            const color = getPaymentMethodColor(data.cell.raw.toLowerCase().replace(' ', '_'));
            data.cell.styles.textColor = color;
            data.cell.styles.fontStyle = 'bold';
        }
        }
    });
    }

    // Status Distribution
    if (reportData.summary.by_status && Object.keys(reportData.summary.by_status).length > 0) {
    doc.addPage();
    doc.setFontSize(18);
    doc.setTextColor(33, 150, 243);
    doc.text('ORDER STATUS DISTRIBUTION', 20, 20);

    const statusData = Object.entries(reportData.summary.by_status).map(([status, data]) => [
        status.toUpperCase(),
        data.count,
        formatCurrency(data.total_sales),
        formatCurrency(data.avg_order_value)
    ]);

    autoTable(doc, {
        startY: 30,
        head: [['Status', 'Count', 'Total Sales', 'Avg Order Value']],
        body: statusData,
        theme: 'grid',
        headStyles: { fillColor: [156, 39, 176], textColor: 255 },
        columnStyles: {
        2: { halign: 'right' },
        3: { halign: 'right' }
        },
        didParseCell: (data) => {
        if (data.column.index === 0 && data.cell.raw) {
            const color = getStatusColor(data.cell.raw.toLowerCase());
            data.cell.styles.textColor = color;
            data.cell.styles.fontStyle = 'bold';
        }
        }
    });
    }

    // Top Selling Items
    if (reportData.summary.top_items && reportData.summary.top_items.length > 0) {
    doc.addPage();
    doc.setFontSize(18);
    doc.setTextColor(33, 150, 243);
    doc.text('TOP SELLING ITEMS', 20, 20);

    const itemsData = reportData.summary.top_items.map(item => [
        item.stock_name || 'Unknown Item',
        formatNumber(item.total_quantity),
        formatCurrency(item.total_sales),
        formatCurrency(item.avg_price),
        `${((item.total_sales / reportData.summary.total_sales) * 100).toFixed(1)}%`
    ]);

    autoTable(doc, {
        startY: 30,
        head: [['Item Name', 'Quantity Sold', 'Total Sales', 'Avg Price', '% of Sales']],
        body: itemsData,
        theme: 'striped',
        headStyles: { fillColor: [255, 152, 0], textColor: 255 },
        columnStyles: {
        1: { halign: 'right' },
        2: { halign: 'right' },
        3: { halign: 'right' },
        4: { halign: 'right' }
        }
    });
    }

    // Top Customers
    if (reportData.top_customers && reportData.top_customers.length > 0) {
    doc.addPage();
    doc.setFontSize(18);
    doc.setTextColor(33, 150, 243);
    doc.text('TOP CUSTOMERS', 20, 20);

    const customersData = reportData.top_customers.map(customer => [
        customer.customer_name || 'Unknown',
        customer.customer_type || 'N/A',
        customer.order_count,
        formatCurrency(customer.total_spent),
        formatCurrency(customer.avg_order_value),
        formatDate(customer.last_order_date)
    ]);

    autoTable(doc, {
        startY: 30,
        head: [['Customer Name', 'Type', 'Orders', 'Total Spent', 'Avg Order', 'Last Order']],
        body: customersData,
        theme: 'striped',
        headStyles: { fillColor: [0, 150, 136], textColor: 255 },
        columnStyles: {
        2: { halign: 'right' },
        3: { halign: 'right' },
        4: { halign: 'right' }
        }
    });
    }

    // Footer on all pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(10);
    doc.setTextColor(128, 128, 128);
    doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10);
    doc.text(companyInfo.name, 20, doc.internal.pageSize.height - 10);
    }

    const fileName = `bar_orders_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    return { success: true, fileName };
} catch (error) {
    console.error('PDF export failed:', error);
    throw new Error('Failed to export to PDF: ' + error.message);
}
};

// Generate CSV Report - Updated
export const exportBarOrdersToCSV = async (reportData, filters = {}) => {
try {
    const csvLines = [];
    
    // Header
    csvLines.push('BAR ORDERS REPORT');
    csvLines.push(`Generated on: ${new Date().toLocaleString()}`);
    csvLines.push(`Company: ${companyInfo.name}`);
    csvLines.push('');
    
    // Summary
    csvLines.push('SUMMARY');
    csvLines.push('Metric,Value');
    csvLines.push(`Total Orders,${reportData.summary.total_orders}`);
    csvLines.push(`Total Sales,${formatCurrency(reportData.summary.total_sales)}`);
    csvLines.push(`Total Items Sold,${reportData.summary.total_items}`);
    csvLines.push(`Average Order Value,${formatCurrency(reportData.summary.avg_order_value)}`);
    csvLines.push('');
    
    // Main Data - Updated columns
    csvLines.push('ORDER DETAILS');
    csvLines.push('Order No.,Date/Time,Customer Type,Total Amount,Payment,Status,Order Notes');
    
    reportData.orders?.forEach(order => {
    csvLines.push([
        `"${formatOrderNumber(order.order_number)}"`,
        `"${formatDateTime(order.created_at)}"`,
        `"${(order.customer_type?.name || 'N/A').replace(/"/g, '""')}"`,
        order.total_amount,
        `"${order.payment_method ? order.payment_method.toUpperCase().replace('_', ' ') : 'PENDING'}"`,
        `"${order.order_status ? order.order_status.toUpperCase() : 'PENDING'}"`,
        `"${(order.order_notes || '').replace(/"/g, '""')}"`
    ].join(','));
    });
    
    // Items Data
    if (reportData.orders?.some(o => o.items?.length > 0)) {
    csvLines.push('');
    csvLines.push('ORDER ITEMS');
    csvLines.push('Order No.,Item Name,Quantity,Unit Price,Total Amount');
    
    reportData.orders.forEach(order => {
        order.items?.forEach(item => {
        csvLines.push([
            `"${formatOrderNumber(order.order_number)}"`,
            `"${(item.stock_name || item.name || 'N/A').replace(/"/g, '""')}"`,
            item.quantity,
            item.price,
            item.total_amount
        ].join(','));
        });
    });
    }

    // Create CSV file
    const csvContent = csvLines.join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `bar_orders_report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
} catch (error) {
    console.error('CSV export failed:', error);
    throw new Error('Failed to export to CSV: ' + error.message);
}
};

// Quick PDF Export - Updated
export const exportBarOrdersQuickPDF = async (reportData) => {
try {
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(18);
    doc.text('Bar Orders Quick Report', 20, 20);
    
    // Summary
    doc.setFontSize(12);
    let y = 40;
    
    const quickSummary = [
    `Total Orders: ${formatNumber(reportData.summary.total_orders)}`,
    `Total Sales: ${formatCurrency(reportData.summary.total_sales)}`,
    `Total Items Sold: ${formatNumber(reportData.summary.total_items)}`,
    `Average Order Value: ${formatCurrency(reportData.summary.avg_order_value)}`,
    `Top Customers: ${formatNumber(reportData.top_customers?.length || 0)}`
    ];
    
    quickSummary.forEach(line => {
    doc.text(line, 20, y);
    y += 10;
    });
    
    y += 10;
    
    // Recent Orders
    if (reportData.orders?.length > 0) {
    doc.text('Recent Orders:', 20, y);
    y += 10;
    
    reportData.orders.slice(0, 5).forEach(order => {
        const line = `• ${formatOrderNumber(order.order_number)}: ${formatCurrency(order.total_amount)} (${order.order_status}) - ${order.customer_type?.name || 'N/A'}`;
        if (line.length > 60) {
        doc.text(line.substring(0, 60), 25, y);
        y += 7;
        doc.text(line.substring(60), 25, y);
        } else {
        doc.text(line, 25, y);
        }
        y += 7;
    });
    }
    
    const fileName = `bar_orders_quick_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    
    return { success: true, fileName };
} catch (error) {
    console.error('Quick PDF export failed:', error);
    throw new Error('Failed to export quick PDF: ' + error.message);
}
};